/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { theme } from "@nordcloud/gnui";
import { TARGET_TYPE } from "~/components/Charts/types";
import { getKpiValueColor } from "~/components/Charts/utils";
import { getKpiType, KpiType } from "~/constants";
import { isNotNil, Maybe, PathName } from "~/tools";
import { KpiSummary } from "../components/KpiSummary";

type SummaryItems = React.ComponentProps<typeof KpiSummary>["items"];

type Props = {
  type: KpiType;
  target: Maybe<number>;
  value: Maybe<string>;
  additionalValue: Maybe<string>;
};

export function useKpiWidget({ type, target, value, additionalValue }: Props) {
  const { label, ...kpiTypeOption } = getKpiType(type);
  const targetType =
    "targetType" in kpiTypeOption ? kpiTypeOption.targetType : undefined;
  const percentageValue = percentageOrNil(value);
  const color = getKpiValueColor({
    kpiValue: parseFloat(percentageValue ?? ""),
    targetType: targetType ?? TARGET_TYPE.negative,
    targetValue: target ?? undefined,
    defaultColor: theme.color.text.text01,
  });

  const icon = getIconName(color);
  const iconWrapperColor = target == null ? theme.color.support.grey : color;
  const formattedKpiPercentage = formatKpiPercentage(target, targetType);
  const items: SummaryItems = [
    {
      key: kpiTypeOption.valueLabel,
      value: formatKpiPercentage(percentageValue),
      color,
      additionalValue: additionalValue ?? undefined,
    },
    {
      key: "Target",
      value: formattedKpiPercentage,
    },
  ];

  return {
    iconWrapperColor,
    items,
    icon,
    label,
    formattedKpiPercentage,
  };
}

function formatKpiPercentage(
  value?: number | string | null,
  targetType?: TARGET_TYPE
) {
  const isPositiveTargetType = targetType === TARGET_TYPE.positive;
  const prefix = isPositiveTargetType ? "<" : ">";
  const showPrefix = targetType != null;
  const printedPrefix = showPrefix ? prefix : "";

  return value == null ? "-" : `${printedPrefix}${value}%`;
}

function getIconName(
  color: string
): Extract<PathName, "emojiHappy" | "emojiNormal" | "emojiSad"> {
  switch (color) {
    case theme.colors.danger:
      return "emojiSad";
    case theme.colors.success:
      return "emojiHappy";
    default:
      return "emojiNormal";
  }
}

// Get percentage with precision to `0.1%` or nil
function percentageOrNil(value?: string | null) {
  return isNotNil(value)
    ? (Math.round(Number(value) * 1000) / 10).toString(10)
    : value;
}
